import * as React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';

import { getAriaSortLabel, getTableSortIcon } from './utils';
import { I18nContext } from '../../i18n';
import { magma } from '../../theme/magma';
import { ThemeContext } from '../../theme/ThemeContext';
import { Checkbox } from '../Checkbox';
import {
  IndeterminateCheckbox,
  IndeterminateCheckboxStatus,
} from '../IndeterminateCheckbox';

import {
  TableContext,
  TableRowColor,
  TableCell,
  TableCellAlign,
  TableDensity,
  TableSortDirection,
} from './';

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  /**
   * @children required
   */
  children: React.ReactNode;
  /**
   * The color scheme of the table row, giving contextual meaning to the content
   */
  color?: TableRowColor;
  headerRowStatus?: IndeterminateCheckboxStatus;
  isSelected?: boolean;
  isSelectableDisabled?: boolean;
  /**
   * Direction by which the column is sorted
   * @default TableSortDirection.none
   */
  sortDirection?: TableSortDirection;
  /**
   * Event that fires when clicking the table header cell sort button
   */
  onSort?: () => void;
  onHeaderRowSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTableRowSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowIndex?: number;
  /**
   * Unique name to be used to identify row for screen readers
   */
  rowName?: string;
  /**
   * @internal
   */
  testId?: string;
}

function buildTableRowBackground(props) {
  if (props.isInverse) {
    switch (props.color) {
      case 'success':
        return props.theme.colors.green500;
      case 'warning':
        return props.theme.colors.yellow400;
      case 'danger':
        return props.theme.colors.red500;
      case 'info':
        return props.theme.colors.blue500;
      default:
        return 'inherit';
    }
  }

  switch (props.color) {
    case 'success':
      return props.theme.colors.success;
    case 'warning':
      return props.theme.colors.yellow400;
    case 'danger':
      return props.theme.colors.danger;
    case 'info':
      return props.theme.colors.info;
    default:
      return 'inherit';
  }
}

function buildTableRowColor(props) {
  if (props.color && props.isInverse) {
    switch (props.color) {
      case 'success':
        return props.theme.colors.green1000;
      case 'warning':
        return props.theme.colors.brand.navy;
      case 'danger':
        return props.theme.colors.red1000;
      case 'info':
        return props.theme.colors.blue1000;
      default:
        return 'inherit';
    }
  }
  if (props.color && !props.isInverse) {
    return props.color === 'warning'
      ? props.theme.colors.brand.navy
      : props.theme.colors.neutral0;
  }

  return 'inherit';
}

function getBorderBottom(isInverse: boolean) {
  return `1px solid ${
    isInverse ? magma.colors.neutral800 : magma.colors.neutral200
  }`;
}

const StyledTableRow = styled.tr<{
  color?: string;
  hasHoverStyles?: boolean;
  hasSquareCorners?: boolean;
  hasTablePagination?: boolean;
  hasZebraStripes?: boolean;
  isInverse?: boolean;
}>`
  border-bottom: ${props => getBorderBottom(props.isInverse)};
  color: inherit;
  display: table-row;
  outline: 0;
  vertical-align: top;

  // Compensates border bottom for all rows (except last one) when table has outside border
  td {
    border-bottom: ${props => getBorderBottom(props.isInverse)};
  }

  &:last-child {
    // Removes border bottom for last row if it's colored
    border-bottom: ${props =>
      props.color || (props.hasZebraStripes && !props.hasTablePagination)
        ? 0
        : `${getBorderBottom(props.isInverse)}`};

    td {
      border-bottom: 0;
    }
    td:first-child {
      border-radius: ${props =>
        props.hasSquareCorners || props.hasTablePagination
          ? '0'
          : `0 0 0 ${props.theme.borderRadius}`};
    }
    td:last-child {
      border-radius: ${props =>
        props.hasSquareCorners || props.hasTablePagination
          ? '0'
          : `0 0 ${props.theme.borderRadius} 0`};
    }
  }

  ${props =>
    !props.color &&
    css`
      &:nth-of-type(even) {
        background: ${props.hasZebraStripes
          ? props.isInverse
            ? props.theme.colors.neutral1000
            : props.theme.colors.neutral150
          : 'none'};
      }
    `};

  ${props =>
    props.hasHoverStyles &&
    !props.color &&
    css`
    &:hover {
      background: ${
        props.isInverse
          ? props.theme.colors.neutral1000
          : props.theme.colors.neutral100
      };
    `}

  ${props =>
    props.color &&
    css`
      background: ${buildTableRowBackground(props)};
      color: ${buildTableRowColor(props)};
    `};
`;

const HeaderStyledCell = styled(TableCell)<{
  density?: TableDensity;
  hasSquareCorners?: boolean;
  isInverse?: boolean;
}>`
  &&& {
    background: ${props =>
      props.isInverse
        ? props.theme.colors.neutral1000
        : props.theme.colors.neutral150};
    border-bottom-width: 2px;
    border-bottom-style: solid;
    border-bottom-color: ${props =>
      props.isInverse
        ? props.theme.colors.neutral800
        : props.theme.colors.neutral200};
    font-weight: bold;
    vertical-align: bottom;
  }

  &&&:first-child {
    border-radius: ${props =>
      props.hasSquareCorners ? '0' : `${props.theme.borderRadius} 0 0 0`};
  }
`;

const SortButton = styled.button<{
  density?: TableDensity;
  isInverse?: boolean;
  textAlign?: TableCellAlign;
}>`
  align-items: flex-end;
  background: none;
  border: 0;
  color: ${props =>
    props.isInverse
      ? props.theme.colors.neutral0
      : props.theme.colors.brand.navy};
  margin: 0;
  text-align: left;
  width: 100%;
  flex: 1 1 auto;

  &:focus {
    outline: 2px solid
      ${props =>
        props.isInverse
          ? props.theme.colors.focusInverse
          : props.theme.colors.focus};
    outline-offset: -2px;
  }

  &:hover,
  &:focus {
    cursor: pointer;
    svg {
      fill: ${props =>
        props.isInverse
          ? props.theme.colors.neutral0
          : props.theme.colors.neutral700};
    }
  }
`;

const SortIconWrapper = styled.span`
  position: relative;
  top: ${props => props.theme.spaceScale.spacing01};
`;

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  (props, ref) => {
    const {
      children,
      headerRowStatus,
      isSelected,
      isSelectableDisabled,
      sortDirection,
      onHeaderRowSelect,
      onTableRowSelect,
      rowIndex,
      rowName,
      onSort,
      testId,
      ...other
    } = props;
    const theme = React.useContext(ThemeContext);
    const tableContext = React.useContext(TableContext);
    const i18n = React.useContext(I18nContext);

    let isHeaderRow = false;

    React.Children.forEach(children, (child: any) => {
      if (child.type.displayName === 'TableHeaderCell') {
        isHeaderRow = true;

        return;
      }
    });

    function getIsCheckboxInverse() {
      if (props.color && props.color === TableRowColor.warning) {
        return false;
      }
      if (props.color && props.color !== TableRowColor.warning) {
        return true;
      }

      return tableContext.isInverse;
    }

    const [isHovering, setIsHovering] = React.useState(false);

    const handleMouseEnter = () => {
      if (tableContext.isSortableBySelected) setIsHovering(true);
    };

    const handleMouseLeave = () => {
      if (tableContext.isSortableBySelected) setIsHovering(false);
    };

    function handleSort() {
      onSort && typeof onSort === 'function' && onSort();
    }

    const SortIcon = getTableSortIcon({
      sortDirection,
      isInverse: tableContext.isInverse,
      theme,
    });

    const sortButtonAriaLabel =
      i18n.table.selectable.sortButtonAriaLabelBySelected +
      ' ' +
      getAriaSortLabel(sortDirection);

    return (
      <StyledTableRow
        {...other}
        data-testid={testId}
        hasHoverStyles={tableContext.hasHoverStyles && !isHeaderRow}
        hasSquareCorners={tableContext.hasSquareCorners}
        hasTablePagination={tableContext.hasTablePagination}
        hasZebraStripes={tableContext.hasZebraStripes}
        isInverse={tableContext.isInverse}
        ref={ref}
        theme={theme}
      >
        {tableContext.isSelectable && isHeaderRow && (
          <HeaderStyledCell
            theme={theme}
            width={theme.spaceScale.spacing05}
            density={tableContext.density}
            hasSquareCorners={tableContext.hasSquareCorners}
            isInverse={tableContext.isInverse}
            style={{
              background: isHovering
                ? transparentize(0.93, theme.colors.neutral900)
                : '',
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'row' }}>
              <IndeterminateCheckbox
                status={headerRowStatus}
                isInverse={getIsCheckboxInverse()}
                labelStyle={{ padding: 0 }}
                labelText={i18n.table.selectable.selectAllRowsAriaLabel}
                isTextVisuallyHidden
                onChange={onHeaderRowSelect}
              />
              {tableContext.isSortableBySelected && (
                <SortButton
                  aria-label={sortButtonAriaLabel}
                  density={tableContext.density}
                  isInverse={tableContext.isInverse}
                  onClick={handleSort}
                  textAlign={TableCellAlign.left}
                  theme={theme}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  data-testid={`${testId || ''}-sort-button`}
                >
                  <SortIconWrapper theme={theme}>{SortIcon}</SortIconWrapper>
                </SortButton>
              )}
            </span>
          </HeaderStyledCell>
        )}
        {tableContext.isSelectable && !isHeaderRow && (
          <TableCell
            width={theme.spaceScale.spacing05}
            style={{ verticalAlign: 'middle' }}
          >
            <Checkbox
              checked={isSelected}
              disabled={isSelectableDisabled}
              labelStyle={{ padding: 0 }}
              labelText={`${i18n.table.selectable.selectRowAriaLabel} ${rowName || ''}`}
              isTextVisuallyHidden
              isInverse={getIsCheckboxInverse()}
              onChange={onTableRowSelect}
            />
          </TableCell>
        )}

        {children}
      </StyledTableRow>
    );
  }
);
